export interface ICookieConsent {
  isConsentRequired: boolean;
  consentState: {
    analytics: boolean;
    socialMedia: boolean;
    advertising: boolean;
  };
}

export interface ICookiesProvider {
  get(key: string): string | undefined;
  getAll(): ReadonlyDictionary<string>;
  set(key: string, value: string, options?: ICookieOptions): void;
  delete(key: string, options?: Pick<ICookieOptions, "domain" | "path">): void;
}

export enum ConsentCategory {
  Analytics = "analytics",
  Advertising = "advertising",
  SocialMedia = "socialMedia",
}

interface IBaseCookieOptions {
  /**
   * An attribute which will be serialized, conformably to RFC 6265
   * section 5.2.
   */
  [property: string]: string[] | string | boolean | number | Date | undefined;

  /**
   * Define the path where the cookie is available. Defaults to '/'
   */
  path?: string;

  /**
   * Define the domain where the cookie is available. Defaults to
   * the domain of the page where the cookie was created.
   */
  domain?: string;

  /**
   * A Boolean indicating if the cookie transmission requires a
   * secure protocol (https). Defaults to true.
   */
  secure?: boolean;

  /**
   * Asserts that a cookie must not be sent with cross-origin requests,
   * providing some protection against cross-site request forgery
   * attacks (CSRF). Defaults to lax.
   */
  sameSite?: "lax" | "strict" | "none";

  /**
   * Indicates whether cookie should be validated before setting.
   */
  essential?: boolean;

  /**
   * Defines category of the cookie being processed
   */
  categories?: ConsentCategory[];
}

interface SessionCookieOptions extends IBaseCookieOptions {
  /**
   * Indicates if the cookie should be a session cookie, which means
   * it will only live as long as the browser is open.
   * Useful for WebView based experiences.
   */
  sessionOnly: true;
}

interface PersistedCookieOptions extends IBaseCookieOptions {
  /**
   * Define when the cookie will be removed. Value can be a
   * Date instance. If omitted, the cookie becomes a session cookie.
   */
  expires?: Date;

  /**
   * Alternate method to define when a cookie will be removed. If used together
   * with expires, expires will be preferred
   */
  maxAgeMilliseconds?: number;
}

export type ICookieOptions = SessionCookieOptions | PersistedCookieOptions;
